import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../../features/auth/authSlice';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import SearchHeader from './components/SearchHeader';
import FilterChips from './components/FilterChips';
import PropertyGrid from './components/PropertyGrid';
import MapView from './components/MapView';
import { useGetCategoriesQuery, useGetUserFavouritePropertyQuery } from '../../features/api/apiSlice';
import usePropertySearch from '../../lib/hooks/usePropertySearch';
import SEO from '../../components/seo';
import { SITE_URL } from '../../lib/seo';
import Icon from '../../components/AppIcon';
import PlatformAdBanner from '../../components/monetization/PlatformAdBanner';

const initialFilters = {
  location: '',
  transactionType: 'all',
  propertyType: 'all',
  priceRange: [0, 500000000],
  status: 'all',
  sortBy: 'relevance',
  bedrooms: 'any',
  bathrooms: 'any',
  sqftRange: [0, 10000],
  yearBuiltRange: [1900, new Date().getFullYear()],
  amenities: [],
  features: [],
  recentlyAdded: 'any',
  hasVirtualTour: false,
  distanceFromCity: 50,
  hoaFeeMax: '',
  lotSizeMin: '',
};

const PropertySearchBrowse = () => {
  const [searchParams] = useSearchParams();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const { data: userFavoriteProperties = [] } = useGetUserFavouritePropertyQuery(undefined, {
    skip: !isAuthenticated,
  });

  const favoritePropertyIds = useMemo(
    () =>
      (userFavoriteProperties || []).map((fav) => fav.property_id || fav.id).filter(Boolean),
    [userFavoriteProperties]
  );

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  const {
    properties,
    isLoading,
    isFetching,
    searchQuery,
    filters,
    totalPages,
    totalResults,
    updateUrlParams,
    handleFiltersChange,
    handleFilterRemove,
    handleClearFilters,
    handlePageChange,
    page,
  } = usePropertySearch(initialFilters, 23);

  const { data: categories = [] } = useGetCategoriesQuery();

  useEffect(() => {
    setViewMode(searchParams.get('view') || 'grid');
  }, [searchParams]);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    updateUrlParams(filters, searchQuery, filters.page, mode);
  };

  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
  };

  const selectedCategory = categories.find(
    (cat) => String(cat.id) === String(filters?.propertyType)
  );
  const searchContext = {
    location: filters?.location,
    propertyType: selectedCategory?.category || filters?.propertyType,
    priceRange:
      filters?.priceMin || filters?.priceMax
        ? `${filters?.priceMin ? 'UGX ' + parseInt(filters?.priceMin)?.toLocaleString() : ''}${
            filters?.priceMin && filters?.priceMax ? ' - ' : ''
          }${filters?.priceMax ? 'UGX ' + parseInt(filters?.priceMax)?.toLocaleString() : ''}`
        : null,
    queryString: '',
  };

  const locationLabel = filters?.location || 'Uganda';
  const typeLabel =
    filters?.transactionType === 'rent'
      ? 'for rent'
      : filters?.transactionType === 'sell'
        ? 'for sale'
        : 'for sale and rent';
  const categoryLabel = selectedCategory?.category
    ? `${selectedCategory.category}s`
    : 'properties';
  const searchTitle = `${categoryLabel} ${typeLabel} in ${locationLabel}`;
  const searchDescription = `Search ${categoryLabel.toLowerCase()} ${typeLabel} in ${locationLabel}. Compare verified HireBroker listings with photos, prices in UGX, and direct agent contact.`;
  const hasNarrowFilters = Boolean(
    filters?.location ||
      (filters?.transactionType && filters?.transactionType !== 'all') ||
      (filters?.propertyType && filters?.propertyType !== 'all') ||
      searchQuery
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={hasNarrowFilters ? searchTitle : 'Find Properties for Sale and Rent in Uganda'}
        description={searchDescription}
        keywords={`property search uganda, ${categoryLabel} ${locationLabel}, homes ${typeLabel} ${locationLabel}, HireBroker`}
        canonicalUrl={`${SITE_URL}/property-search-browse`}
        robots={hasNarrowFilters ? 'noindex,follow' : 'index,follow,max-image-preview:large'}
      />
      <Header />
      <main className="pt-16">
        <SearchHeader
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          resultsCount={totalResults}
          filters={filters}
          categories={categories}
          onFiltersChange={handleFiltersChange}
          onSearch={() => {}}
          isLoading={isLoading || isFetching}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Breadcrumbs searchContext={searchContext} className="mb-4" />

          <FilterChips
            filters={filters}
            categories={categories}
            onRemoveFilter={handleFilterRemove}
            onClearAll={handleClearFilters}
          />

          <div className="mt-4 mb-2">
            <PlatformAdBanner placement="search_sidebar" />
          </div>

          <div className="mt-4 flex gap-6">
            <div className="flex-1 min-w-0">
              {viewMode === 'grid' ? (
                <PropertyGrid
                  properties={properties}
                  loading={isLoading || isFetching}
                  favoritePropertyIds={favoritePropertyIds}
                  currentPage={page}
                  totalPages={totalPages}
                  totalResults={totalResults}
                  onPageChange={handlePageChange}
                />
              ) : (
                <div className="h-[600px] lg:h-[720px] rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white">
                  <MapView
                    properties={properties}
                    onPropertySelect={handlePropertySelect}
                    selectedProperty={selectedProperty}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Secondary CTA */}
          <div className="mt-12 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-violet-50 text-violet-700 flex items-center justify-center flex-shrink-0">
                <Icon name="ClipboardList" size={20} />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Looking for something specific?</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Post a Specific Property Request and let verified agents match you.
                </p>
              </div>
            </div>
            <Link
              to="/notice-board"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            >
              Specific Property Request
              <Icon name="ArrowRight" size={16} />
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PropertySearchBrowse;
